import type { LscMatch } from './types';

export const ALL_SEASONS_FILTER = 'All seasons';
export const ALL_WEEKS_FILTER = 'All weeks';
export const ALL_TEAMS_FILTER = 'All teams';

const PLAYOFF_STAGE_ORDER: Record<string, number> = {
  'wild card': 100,
  divisional: 101,
  conference: 102,
  'super bowl': 103,
};

/** Numeric sort key for stage/week labels (lower = earlier in season). */
export function parseStageSortKey(stageLabel?: string | null): number {
  const normalized = (stageLabel ?? '').trim().toLowerCase();
  if (!normalized) {
    return 9999;
  }

  if (/pre[- ]?season/.test(normalized)) {
    return 0;
  }

  const weekMatch = normalized.match(/week\s*0*(\d{1,2})\b/i);
  if (weekMatch) {
    return Number.parseInt(weekMatch[1], 10);
  }

  for (const [label, order] of Object.entries(PLAYOFF_STAGE_ORDER)) {
    if (normalized.includes(label)) {
      return order;
    }
  }

  return 5000;
}

export function parseSeasonSortKey(seasonLabel?: string | null): number {
  const match = (seasonLabel ?? '').match(/(\d+)/);
  return match ? Number.parseInt(match[1], 10) : 0;
}

function compareStrings(a?: string | null, b?: string | null): number {
  return (a ?? '').localeCompare(b ?? '', undefined, { sensitivity: 'base' });
}

function compareScheduledAt(a?: string | null, b?: string | null): number {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  const aTime = Date.parse(a);
  const bTime = Date.parse(b);
  if (Number.isNaN(aTime) && Number.isNaN(bTime)) return compareStrings(a, b);
  if (Number.isNaN(aTime)) return 1;
  if (Number.isNaN(bTime)) return -1;
  return aTime - bTime;
}

/** Deterministic chronological sort for League Schedules rows. */
export function sortLeagueScheduleMatches(matches: LscMatch[]): LscMatch[] {
  return [...matches].sort((left, right) => {
    const seasonDelta =
      parseSeasonSortKey(left.season_label) - parseSeasonSortKey(right.season_label);
    if (seasonDelta !== 0) {
      return seasonDelta;
    }

    const stageDelta = parseStageSortKey(left.stage_label) - parseStageSortKey(right.stage_label);
    if (stageDelta !== 0) {
      return stageDelta;
    }

    const scheduledDelta = compareScheduledAt(left.scheduled_at, right.scheduled_at);
    if (scheduledDelta !== 0) {
      return scheduledDelta;
    }

    const awayDelta = compareStrings(left.away_side_label, right.away_side_label);
    if (awayDelta !== 0) {
      return awayDelta;
    }

    const homeDelta = compareStrings(left.home_side_label, right.home_side_label);
    if (homeDelta !== 0) {
      return homeDelta;
    }

    return compareStrings(left.id, right.id);
  });
}

export function extractSeasonLabels(matches: LscMatch[]): string[] {
  const labels = new Set<string>();
  for (const match of matches) {
    const label = (match.season_label ?? '').trim();
    if (label) {
      labels.add(label);
    }
  }
  return Array.from(labels).sort((left, right) => {
    const delta = parseSeasonSortKey(left) - parseSeasonSortKey(right);
    return delta !== 0 ? delta : left.localeCompare(right);
  });
}

export function defaultSeasonFilter(seasonLabels: string[]): string {
  if (seasonLabels.length === 0) {
    return ALL_SEASONS_FILTER;
  }
  return seasonLabels[seasonLabels.length - 1];
}

export function filterMatchesBySeason(matches: LscMatch[], seasonFilter: string): LscMatch[] {
  if (seasonFilter === ALL_SEASONS_FILTER) {
    return matches;
  }
  return matches.filter((match) => (match.season_label ?? '').trim() === seasonFilter);
}

export function extractWeekLabels(matches: LscMatch[], seasonFilter?: string): string[] {
  const labels = new Set<string>();
  for (const match of matches) {
    if (seasonFilter && seasonFilter !== ALL_SEASONS_FILTER) {
      if ((match.season_label ?? '').trim() !== seasonFilter) continue;
    }
    const label = (match.stage_label ?? '').trim();
    if (label) labels.add(label);
  }
  return Array.from(labels).sort((left, right) => {
    const delta = parseStageSortKey(left) - parseStageSortKey(right);
    return delta !== 0 ? delta : left.localeCompare(right);
  });
}

export function extractTeamLabels(matches: LscMatch[]): string[] {
  const labels = new Set<string>();
  for (const match of matches) {
    for (const side of [match.away_side_label, match.home_side_label]) {
      const label = (side ?? '').trim();
      if (label) labels.add(label);
    }
  }
  return Array.from(labels).sort((left, right) => left.localeCompare(right));
}

export function defaultWeekFilter(weekLabels: string[], activeStageLabel?: string | null): string {
  const active = (activeStageLabel ?? '').trim();
  if (active && weekLabels.includes(active)) {
    return active;
  }
  if (weekLabels.length === 0) {
    return ALL_WEEKS_FILTER;
  }
  return weekLabels[weekLabels.length - 1];
}

export function filterMatchesByWeek(matches: LscMatch[], weekFilter: string): LscMatch[] {
  if (weekFilter === ALL_WEEKS_FILTER) {
    return matches;
  }
  return matches.filter((match) => (match.stage_label ?? '').trim() === weekFilter);
}

export function filterMatchesByTeam(matches: LscMatch[], teamFilter: string): LscMatch[] {
  if (teamFilter === ALL_TEAMS_FILTER) {
    return matches;
  }
  const needle = teamFilter.trim().toLowerCase();
  return matches.filter((match) => {
    const away = (match.away_side_label ?? '').trim().toLowerCase();
    const home = (match.home_side_label ?? '').trim().toLowerCase();
    return away === needle || home === needle;
  });
}

export function filterMatchesBySearch(matches: LscMatch[], query: string): LscMatch[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return matches;
  return matches.filter((match) => {
    const haystack = [
      match.season_label,
      match.stage_label,
      match.away_side_label,
      match.home_side_label,
      match.away_identity_value,
      match.home_identity_value,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return haystack.includes(needle);
  });
}
